import React from 'react';
import { ColorPicker } from '../views/color-picker';
import { TextBox } from '../views/text-box';
import { TextTestLogic, IColorScheme, IFontFamily } from '../logic/text-test-logic';
import { DebugBox } from '../../shared/components/debug-box/debug-box';
import { GlobalLogic } from '../../shared/logic/global-logic';
import { FontFamilyPicker } from '../views/font-family-picker';
import { VidebisButton } from '../../shared/components/videbis-button/videbis-button';
import { getQuery } from '../../shared/util/functions';
import '../styles/text-test.scss';
import { getVergroesserungsbedarfTelemetryData } from '../../result/util/telemetry';
import { postTelemetry } from '../../result/util/axios';

interface Props {
	history: any;
	location: any;
}

interface State {
	colorScheme: IColorScheme;
	fontSizeIndex: number;
	fontFamily: IFontFamily;
}

export class TextTestController extends React.PureComponent<Props, State> {

	private _textTestLogic: TextTestLogic = TextTestLogic.getInstance();
	private _globalLogic: GlobalLogic = GlobalLogic.getInstance();

	state: State = {
		colorScheme: this._textTestLogic.currentColorScheme,
		fontSizeIndex: this._textTestLogic.currentFontSizeIndex,
		fontFamily: this._textTestLogic.currentFontFamily
	};

	constructor(props: Props) {
		super(props);
		this._textTestLogic.resetState();
	}

	componentDidMount() {
		this.setState({
			colorScheme: this._textTestLogic.currentColorScheme,
			fontSizeIndex: this._textTestLogic.currentFontSizeIndex,
			fontFamily: this._textTestLogic.currentFontFamily
		});
	}

	render() {
		const content: any = this.getContent();
		const debugBox: any = this.getDebugBox();
		return (
			<>
				<main className={'text-test--main'}>
					<section className={'limit-width'}>
						<div className={'text-test--inner'}>
							<h1>Vergrößerungsbedarf</h1>
							{content}
						</div>
					</section>
				</main>
				{debugBox}
			</>
		);
	}

	private handleColorSchemeChange = (pickedColorScheme: IColorScheme): void => {
		this.setState({ colorScheme: pickedColorScheme });
		this._textTestLogic.currentColorScheme = pickedColorScheme;
	};

	private handleFontSizeChange = (decrease: boolean = false): void => {
		let newFontSize = this.state.fontSizeIndex + (decrease ? -1 : 1);

		if (newFontSize < 0) {
			newFontSize = 0;
		}

		if (newFontSize > 16) {
			newFontSize = 16;
		}

		this.setState({ fontSizeIndex: newFontSize });
		this._textTestLogic.currentFontSizeIndex = newFontSize;
	};

	private handleFontFamilyChange = (pickedFontFamily: IFontFamily): void => {
		this.setState({ fontFamily: pickedFontFamily });
		this._textTestLogic.currentFontFamily = pickedFontFamily;
	};

	private handleFinished = (): void => {
		this._textTestLogic.markTestAsFinished();
		this._textTestLogic.updateData(this.state.colorScheme, this.state.fontFamily, this.state.fontSizeIndex);
		const query = getQuery(this.props.location.search);
		if (query['all-tests']) {
			postTelemetry('vergroesserungsbedarf', getVergroesserungsbedarfTelemetryData());
			this.props.history.push('/result?from=all-tests');
		} else {
			this.props.history.push('/result?from=text-test');
		}
	};

	private handleBack = (): void => {
		this.props.history.push('/test-explanation?test=text-test');
	};

	private getContent = (): JSX.Element => {
		/*
		 * TODO: Why do we apply the viewer's distance to the font-size? Should it not only be considered when
		 * calculating the result? The font-size should be the same font-size, only the results may differ depending on
		 * the viewer's distance to the screen. Or do I have an error in reasoning here?
		 */
		return (
			<section className={'text-test__content'}>
				<ColorPicker
					fontFamily={this.state.fontFamily.cssEntity}
					onSchemeChange={this.handleColorSchemeChange}
					current={this.state.colorScheme}
				/>
				<TextBox
					bgColor={this.state.colorScheme.bgColor}
					fontSize={TextTestLogic.fontSizes[this.state.fontSizeIndex].fontSize * (this._globalLogic.distance / 25)}
					text={TextTestLogic.fontSizes[this.state.fontSizeIndex].text}
					onFontSizeChange={this.handleFontSizeChange}
					fontColor={this.state.colorScheme.txtColor}
					fontFamily={this.state.fontFamily.cssEntity}
				/>
				<FontFamilyPicker
					onFontFamilyChange={this.handleFontFamilyChange}
					selected={this.state.fontFamily}
				/>
				<div className={'text-test__buttons'}>
					<VidebisButton
						onPress={this.handleBack}
						text={'Zurück'}
						bgColor={'#ffffff'}
						txtColor={'#0c23fb'} />
					<VidebisButton onPress={this.handleFinished} text={'Fertig'} />
				</div>
			</section>
		);
	};

	private getDebugBox = (): JSX.Element => {
		return (
			<DebugBox
				content={{
					'Font Size (in pt)': TextTestLogic.fontSizes[this.state.fontSizeIndex].fontSize,
					'Distance:': this._globalLogic.distance,
					'Scale Factor': parseFloat(this._globalLogic.scaleFactor.toFixed(2)),
					'Font Color': this.state.colorScheme.txtColor,
					'Background Color': this.state.colorScheme.bgColor,
					'Font Family': this.state.fontFamily.name
				}}
			/>
		);
	};
}
